import { useNavigate } from 'react-router-dom';
import { Building2, FolderKanban, Users, BookUser, ArrowRight } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Button, Badge } from '@vnetwork/ui/primitives';
import { ORG_PRODUCT, orgConsoleRoute, markOrgIntroSeen } from './organizations-service';
import { OnboardingCss } from './onboarding-css';
import {
  heroWrap,
  heroIcon,
  heroEyebrow,
  heroTitle,
  heroLede,
  stepList,
  stepRow,
  markerAccent,
  stepTitle,
  stepDesc,
} from './onboarding-styles';

/**
 * Organizations first-run intro — the one-time landing shown before the console.
 * Uses the same hero visual language as the OSS / WAAP onboarding, but there is
 * NOTHING to provision: every account intrinsically has an organization, so the
 * CTA simply marks the intro seen and opens the console (no POST /services, no
 * vendor tenant, no zone). Subsequent visits skip straight to the console.
 */
export function OrganizationsActivateLanding({ onBack }: { onBack?: () => void }) {
  const navigate = useNavigate();

  function openConsole() {
    markOrgIntroSeen();
    navigate(orgConsoleRoute());
  }

  return (
    <Container size="xl">
      <PageHeader
        icon={<Building2 size={22} />}
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            Organizations
            <Badge tone="info" appearance="subtle" size="sm">
              Beta
            </Badge>
          </span>
        }
        description={ORG_PRODUCT.description}
        actions={
          onBack ? (
            <Button variant="ghost" onClick={onBack}>
              Back to services
            </Button>
          ) : undefined
        }
      />

      <div className="vn-ob-hero" style={heroWrap}>
        <OnboardingCss />

        <span className="vn-ob-halo" style={heroIcon} aria-hidden>
          <Building2 size={30} />
        </span>

        <p style={heroEyebrow}>Organizations</p>
        <h2 style={heroTitle}>Manage your organization</h2>
        <p style={heroLede}>
          Projects, teams, contacts and administrators — all your organization management in one
          place, with fine-grained permissions.
        </p>

        <Button
          size="lg"
          endIcon={<ArrowRight size={16} />}
          onClick={openConsole}
          style={{ marginTop: 'var(--vn-space-2)' }}
        >
          Open Organizations
        </Button>

        <div style={{ ...stepList, marginTop: 'var(--vn-space-5)' }}>
          {PERKS.map((p) => (
            <div key={p.title} style={stepRow}>
              <span style={markerAccent}>{p.icon}</span>
              <div style={{ display: 'grid', gap: 2 }}>
                <p style={stepTitle}>{p.title}</p>
                <p style={stepDesc}>{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Container>
  );
}

const PERKS = [
  {
    icon: <FolderKanban size={16} />,
    title: 'Projects',
    desc: 'Group domains and instances into projects with per-team roles.',
  },
  {
    icon: <Users size={16} />,
    title: 'Teams',
    desc: 'Invite members and grant fine-grained, scoped permissions.',
  },
  {
    icon: <BookUser size={16} />,
    title: 'Contacts & admins',
    desc: 'Manage organization contacts and administrators.',
  },
];

export default OrganizationsActivateLanding;
